import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import { db } from "../firebase";
import { doc, getDoc, getDocs, collection } from "firebase/firestore";
import { useCart } from "../context/CartContext";
import {
    FiShoppingBag, FiArrowLeft, FiTag, FiBookOpen, FiHash,
    FiFacebook, FiTwitter, FiLink, FiGlobe
} from "react-icons/fi";
import ReviewSystem from "../components/ReviewSystem";
import { bundleDeliveryOf } from "../utils/bundles";
import toast from "react-hot-toast";

/* ── Stock helpers ───────────────────────────────────────── */
const NON_PHYSICAL_FORMATS = ["eBook", "Audiobook", "Bundle"];

// Copies a book has for bundles: its Paperback count if it has one, otherwise the first physical format. null = unlimited.
function physicalCopiesLeft(book) {
    const variants = book.variants || [];
    if (variants.length === 0) return typeof book.stock === "number" ? book.stock : null;
    const paperback = variants.find(v => v.format === "Paperback" && typeof v.stock === "number");
    const physical = paperback || variants.find(v => !NON_PHYSICAL_FORMATS.includes(v.format) && typeof v.stock === "number");
    return physical ? physical.stock : null;
}

// A bundle can be bought while every book inside it has a copy left. Digital-only bundles need no copies.
function bundleAvailable(bundleVariant, bookStock) {
    if (bundleDeliveryOf(bundleVariant) === "Digital") return true;
    return (bundleVariant.bundleItems || []).every(b => {
        const left = bookStock[b.id];
        return left === undefined || left === null || left > 0; // undefined = still loading
    });
}

/* ── Format helpers ──────────────────────────────────────── */
const FORMAT_ICONS = {
    "eBook": "📱",
    "Paperback": "📖",
    "Hardcover": "📚",
    "Deluxe Edition": "✨",
    "Bundle": "📦",
};

export default function ProductDetails() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
    const { addItem } = useCart();
    // Stock left for each book inside a bundle (null = unlimited). Bundles have no stock of their own.
    const [bookStock, setBookStock] = useState({});
    // Bundles (from any product) that contain this book, so the book page can point to them
    const [containingBundles, setContainingBundles] = useState([]);

    useEffect(() => {
        if (!product) return;
        let cancelled = false;
        getDocs(collection(db, "products"))
            .then(snap => {
                const found = [];
                const stockById = {};
                snap.forEach(d => { stockById[d.id] = physicalCopiesLeft(d.data()); });
                snap.forEach(d => {
                    if (d.id === product.id) return; // this product's own bundles are already on the page
                    const data = d.data();
                    (data.variants || []).forEach(v => {
                        if (v.format !== "Bundle") return;
                        const items = v.bundleItems || [];
                        if (!items.some(b => b.id === product.id)) return;
                        found.push({
                            key: `${d.id}_${bundleDeliveryOf(v) || "Physical"}`,
                            parentId: d.id,
                            parentTitle: data.title,
                            parentAuthor: data.author,
                            parentImage: data.imageUrl,
                            parentLanguage: data.language,
                            variant: v,
                            name: v.bundleName || `${data.title} Bundle`,
                            delivery: bundleDeliveryOf(v) === "Combo" ? "Physical + Digital" : (bundleDeliveryOf(v) || "Physical"),
                            price: v.price,
                            others: items.filter(b => b.id !== product.id).map(b => b.title),
                            imageUrl: v.bundleImageUrl || "",
                            available: bundleDeliveryOf(v) === "Digital" || items.every(b => {
                                const left = stockById[b.id];
                                return left === undefined || left === null || left > 0;
                            }),
                        });
                    });
                });
                if (!cancelled) setContainingBundles(found);
            })
            .catch(e => console.error("Error loading related bundles:", e));
        return () => { cancelled = true; };
    }, [product]);

    useEffect(() => {
        if (!product || !product.variants) return;
        const bookIds = [...new Set(
            product.variants
                .filter(v => v.format === "Bundle" && bundleDeliveryOf(v) !== "Digital")
                .flatMap(v => (v.bundleItems || []).map(b => b.id))
        )];
        if (bookIds.length === 0) return;
        let cancelled = false;
        Promise.all(bookIds.map(async bookId => {
            const snap = await getDoc(doc(db, "products", bookId));
            return [bookId, snap.exists() ? physicalCopiesLeft(snap.data()) : 0];
        }))
            .then(entries => { if (!cancelled) setBookStock(Object.fromEntries(entries)); })
            .catch(e => console.error("Error checking bundle stock:", e));
        return () => { cancelled = true; };
    }, [product]);

    useEffect(() => {
        async function fetchProduct() {
            try {
                const snap = await getDoc(doc(db, "products", id));
                if (snap.exists()) {
                    setProduct({ id: snap.id, ...snap.data() });
                    setSelectedVariantIdx(0);
                }
            } catch (e) {
                console.error("Error fetching product:", e);
            }
            setLoading(false);
        }
        fetchProduct();
    }, [id]);

    if (loading) return (
        <div style={{ padding: "100px 0", textAlign: "center", color: "#888" }}>
            Loading book details...
        </div>
    );

    if (!product) return (
        <div style={{ padding: "100px 20px", textAlign: "center" }}>
            <h2>Book not found</h2>
            <Link to="/shop" style={{ color: "var(--accent)" }}>Back to Shop</Link>
        </div>
    );

    // Add a bundle that lives on another product (shown on this book's page)
    function addContainingBundle(b) {
        const v = b.variant;
        const delivery = bundleDeliveryOf(v) || "Physical";
        addItem({
            productId: b.parentId,
            id: b.parentId,
            cartId: `${b.parentId}_Bundle_${delivery}`,
            title: v.bundleName || `${b.parentTitle} Bundle`,
            author: b.parentAuthor || "",
            imageUrl: b.parentImage || "",
            language: b.parentLanguage || null,
            selectedFormat: "Bundle",
            price: v.price,
            isDigital: delivery === "Digital",
            isPreorder: false,
            releaseDate: null,
            bundleDelivery: delivery,
            bundleItems: v.bundleItems || [],
            epubStoragePath: "",
            epubFileName: "",
            metaContentId: null,
        });
        toast.success("Bundle added to cart! 🎉");
    }

    /* ── Derive the active "edition" ─────────────────────── */
    const hasVariants = product.variants && product.variants.length > 0;

    // The currently selected variant object (or a synthetic one for legacy products)
    const activeVariant = hasVariants
        ? product.variants[selectedVariantIdx]
        : {
            format: product.format || "Paperback",
            price: product.price,
            stock: product.stock,
            isDigital: product.isDigital,
            epubStoragePath: product.epubStoragePath,
            bundleItems: product.bundleItems || [],
        };

    const isDigital = activeVariant.isDigital || activeVariant.format === "eBook";
    const isBundle = activeVariant.format === "Bundle";
    const bundleSoldOut = isBundle && !bundleAvailable(activeVariant, bookStock);
    const outOfStock = bundleSoldOut || (!isDigital && !isBundle && !product.isPreorder && (activeVariant.stock ?? 0) <= 0);

    /* ── Language display ────────────────────────────────── */
    const langMap = {
        Spanish: { flag: "🇵🇷", label: "Español" },
        English: { flag: "🇺🇸", label: "English" },
    };
    const langInfo = langMap[product.language] || null;

    /* ── SEO ─────────────────────────────────────────────── */
    const seoData = {
        title: product.seoTitle || `${product.title} by ${product.author}`,
        description: product.seoDescription || (product.description
            ? product.description.replace(/<[^>]*>/g, "").substring(0, 160)
            : ""),
        keywords: product.seoKeywords || "",
        image: product.imageUrl,
        price: activeVariant?.price || product.price,
        currency: "USD"
    };

    /* ── Add to cart ─────────────────────────────────────── */
    function handleAddToCart() {
        const isBundle = activeVariant.format === "Bundle";
        const isDigitalBundle = isBundle && bundleDeliveryOf(activeVariant) === "Digital";
        const isDigital = activeVariant.isDigital || activeVariant.format === "eBook" || isDigitalBundle;

        const cartItem = {
            productId: product.id,
            id: product.id, // backward-compat
            // Bundles can exist more than once per product (physical, digital, combo), so include the delivery type
            cartId: isBundle ? `${product.id}_Bundle_${bundleDeliveryOf(activeVariant) || "Physical"}` : `${product.id}_${activeVariant.format}`,
            title: isBundle && activeVariant.bundleName ? activeVariant.bundleName : product.title,
            author: product.author,
            imageUrl: product.imageUrl,
            language: product.language || null,
            selectedFormat: activeVariant.format,
            price: activeVariant.price,
            isDigital: isDigital,
            isPreorder: product.isPreorder || false,
            releaseDate: product.releaseDate || null,
            bundleDelivery: isBundle ? (bundleDeliveryOf(activeVariant) || "Physical") : null,
            // Pass bundle items if this is a bundle variant
            bundleItems: isBundle ? (activeVariant.bundleItems || []) : [],
            epubStoragePath: isDigital ? (activeVariant.epubStoragePath || "") : "",
            epubFileName: isDigital ? (activeVariant.epubFileName || "") : "",
            metaContentId: product.metaContentId || null,
        };
        addItem(cartItem);
        toast.success(product.isPreorder ? "Pre-order added to cart!" : `${activeVariant.format} added to cart!`);
    }

    return (
        <div className="product-details-page" style={{ padding: "60px 20px", minHeight: "80vh" }}>
            <SEO pageId="shop" manualData={seoData} />
            <div className="container">
                <Link to="/shop" style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#666", textDecoration: "none", marginBottom: 40, fontWeight: 500 }}>
                    <FiArrowLeft /> Back to catalog
                </Link>

                <div style={{ display: "flex", gap: 60, flexWrap: "wrap" }}>
                    {/* ── Left: Image ── */}
                    <div className="product-image-side" style={{ flex: "1 1 380px" }}>
                        <div style={{ position: "sticky", top: 100 }}>
                            <img
                                src={product.imageUrl}
                                alt={product.title}
                                style={{ width: "100%", borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.15)" }}
                            />
                            {/* Language pill below image */}
                            {langInfo && (
                                <div style={{
                                    marginTop: 16, display: "inline-flex", alignItems: "center", gap: 8,
                                    background: "#f0e6ff", color: "#7c3aed",
                                    padding: "8px 16px", borderRadius: 30, fontWeight: 700, fontSize: 14
                                }}>
                                    <FiGlobe size={14} /> {langInfo.flag} {langInfo.label}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Right: Info ── */}
                    <div className="product-info-side" style={{ flex: "1 1 460px" }}>
                        {/* Badges row */}
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
                            {langInfo && (
                                <span style={{
                                    background: "#7c3aed", color: "#fff",
                                    padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700
                                }}>
                                    {langInfo.flag} {langInfo.label}
                                </span>
                            )}
                            {product.isFeatured && (
                                <span className="status-badge" style={{ background: "#fff8e1", color: "#f59f00" }}>
                                    ⭐ Featured
                                </span>
                            )}
                            {product.isPreorder && (
                                <span className="status-badge" style={{ background: "#fff3e0", color: "#e65100" }}>
                                    🕒 Pre-order
                                </span>
                            )}
                        </div>

                        <h1 style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)", color: "var(--primary)", margin: "0 0 8px", lineHeight: 1.1 }}>
                            {product.title}
                        </h1>
                        <p style={{ fontSize: "1.15rem", color: "#666", marginBottom: 24 }}>By {product.author}</p>

                        {/* Meta row */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, marginBottom: 32, padding: "20px 0", borderTop: "1px solid #eee", borderBottom: "1px solid #eee" }}>
                            {product.series && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#555", fontSize: 14 }}>
                                    <FiBookOpen style={{ color: "var(--accent)" }} />
                                    <strong>Series:</strong> {product.series}
                                </div>
                            )}
                            {product.bookNumber && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#555", fontSize: 14 }}>
                                    <FiHash style={{ color: "var(--accent)" }} />
                                    <strong>Book:</strong> #{product.bookNumber}
                                </div>
                            )}
                            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#555", fontSize: 14 }}>
                                <FiTag style={{ color: "var(--accent)" }} />
                                <strong>Category:</strong> {product.category}
                            </div>
                        </div>

                        {/* ── VARIANT SELECTOR ── */}
                        {hasVariants && (
                            <div style={{ marginBottom: 36 }}>
                                <p style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#888", marginBottom: 14 }}>
                                    Choose Your Edition
                                </p>
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    {product.variants.map((v, i) => {
                                        const isSelected = i === selectedVariantIdx;
                                        const soldOut = v.format === "Bundle"
                                            ? !bundleAvailable(v, bookStock)
                                            : !v.isDigital && !product.isPreorder && (v.stock ?? 0) <= 0;
                                        return (
                                            <button
                                                key={i}
                                                onClick={() => !soldOut && setSelectedVariantIdx(i)}
                                                disabled={soldOut}
                                                style={{
                                                    padding: "12px 22px",
                                                    borderRadius: 12,
                                                    border: isSelected ? "2px solid var(--accent)" : "2px solid #e0ddd8",
                                                    background: isSelected ? "var(--accent)" : "#fff",
                                                    color: isSelected ? "#fff" : soldOut ? "#bbb" : "#333",
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    cursor: soldOut ? "not-allowed" : "pointer",
                                                    transition: "all 0.2s",
                                                    opacity: soldOut ? 0.6 : 1,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    gap: 4,
                                                    minWidth: 120,
                                                }}
                                            >
                                                <span style={{ fontSize: 20 }}>
                                                    {v.format === "Bundle" && bundleDeliveryOf(v) === "Digital" ? "📱" : v.format === "Bundle" && bundleDeliveryOf(v) === "Combo" ? "🎁" : (FORMAT_ICONS[v.format] || "📘")}
                                                </span>
                                                <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                    <span>{v.format === "Bundle" && v.bundleName ? v.bundleName : v.format}</span>
                                                    {v.format === "Bundle" && (
                                                        <span style={{ fontSize: 10, opacity: 0.8, textTransform: "uppercase" }}>
                                                            {bundleDeliveryOf(v) === "Combo" ? "Physical + Digital" : (bundleDeliveryOf(v) || "Physical")}
                                                        </span>
                                                    )}
                                                </span>
                                                <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.85 }}>
                                                    ${parseFloat(v.price).toFixed(2)}
                                                </span>
                                                {soldOut && <span style={{ fontSize: 10, color: "#e74c3c" }}>Out of stock</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Description */}
                        <div style={{ marginBottom: 40 }}>
                            <h3 style={{ fontSize: "1rem", textTransform: "uppercase", letterSpacing: 1, color: "#888", marginBottom: 16 }}>Description</h3>

                            {product.isPreorder && product.releaseDate && (
                                <div style={{ background: "#fff3e0", borderLeft: "5px solid #e65100", padding: "16px 20px", borderRadius: 8, marginBottom: 30, display: "flex", alignItems: "center", gap: 15 }}>
                                    <span style={{ fontSize: "1.5rem" }}>🕒</span>
                                    <div>
                                        <h4 style={{ margin: 0, color: "#e65100", fontSize: "1.1rem" }}>Pre-order Item</h4>
                                        <p style={{ margin: "4px 0 0", color: "#666", fontSize: "0.95rem" }}>
                                            Scheduled for release on <strong>{new Date(product.releaseDate + "T12:00:00").toLocaleDateString(undefined, { dateStyle: "long" })}</strong>. Secure your copy now!
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div
                                className="rich-text-content"
                                dangerouslySetInnerHTML={{ __html: product.description }}
                                style={{ lineHeight: 1.8, color: "#444" }}
                            />
                        </div>

                        {/* ── Bundle name and picture ── */}
                        {isBundle && activeVariant.bundleName && (
                            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "2rem", color: "var(--primary)", margin: "0 0 20px" }}>
                                {activeVariant.bundleName}
                            </h2>
                        )}
                        {isBundle && activeVariant.bundleDescription && (
                            <p style={{ fontSize: 16, color: "#444", lineHeight: 1.6, margin: "0 0 24px", whiteSpace: "pre-line" }}>
                                {activeVariant.bundleDescription}
                            </p>
                        )}
                        {isBundle && activeVariant.bundleImageUrl && (
                            <img
                                src={activeVariant.bundleImageUrl}
                                alt={`${activeVariant.bundleName || product.title} contents`}
                                style={{ width: "100%", borderRadius: 14, marginBottom: 24, objectFit: "cover" }}
                            />
                        )}

                        {/* ── Bundle contents preview ── */}
                        {isBundle && activeVariant.bundleItems && activeVariant.bundleItems.length > 0 && (
                            <div style={{ background: "#f9f5ff", border: "1.5px solid #d8b4fe", borderRadius: 14, padding: 20, marginBottom: 32 }}>
                                <p style={{ fontWeight: 700, color: "#7c3aed", marginBottom: 14, fontSize: 14 }}>
                                    📦 This bundle includes:
                                </p>
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    {activeVariant.bundleItems.map((bi, idx) => (
                                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            {bi.imageUrl && (
                                                <img src={bi.imageUrl} alt={bi.title} style={{ width: 36, height: 46, objectFit: "cover", borderRadius: 4 }} />
                                            )}
                                            <div>
                                                <p style={{ fontWeight: 600, margin: 0, fontSize: 14 }}>{bi.title}</p>
                                                <p style={{ color: "#888", fontSize: 12, margin: 0 }}>{bi.author}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── Bundles that include this book ── */}
                        {containingBundles.length > 0 && (
                            <div style={{ marginBottom: 32 }}>
                                <p style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#7c3aed", marginBottom: 12 }}>
                                    📦 Bundles with this book
                                </p>
                                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                    {containingBundles.map(b => (
                                        <div
                                            key={b.key}
                                            style={{ display: "flex", gap: 14, alignItems: "center", color: "#333", background: "#f9f5ff", border: "1.5px solid #d8b4fe", borderRadius: 12, padding: 14 }}
                                        >
                                            <Link to={`/book/${b.parentId}`} style={{ display: "flex", gap: 14, alignItems: "center", flex: 1, minWidth: 0, textDecoration: "none", color: "#333" }}>
                                                {b.imageUrl && (
                                                    <img src={b.imageUrl} alt={b.name} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
                                                )}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <p style={{ margin: 0, fontWeight: 700 }}>{b.name}</p>
                                                    <p style={{ margin: "4px 0 0", fontSize: 13, color: "#666" }}>
                                                        {b.delivery} · ${Number(b.price).toFixed(2)}
                                                    </p>
                                                    {b.others.length > 0 && (
                                                        <p style={{ margin: "6px 0 0", fontSize: 13, color: "#444" }}>
                                                            Also includes: {b.others.join(", ")}
                                                        </p>
                                                    )}
                                                </div>
                                            </Link>
                                            <button
                                                type="button"
                                                onClick={() => addContainingBundle(b)}
                                                disabled={!b.available}
                                                style={{ flexShrink: 0, padding: "10px 16px", borderRadius: 10, border: "none", background: b.available ? "var(--accent)" : "#bbb", color: "#fff", fontWeight: 700, fontSize: 13, cursor: b.available ? "pointer" : "not-allowed" }}
                                            >
                                                {b.available ? "Add to Cart" : "Out of Stock"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── Price + Add to Cart ── */}
                        <div style={{ background: "#f9f9f9", padding: 30, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                            <div>
                                <span style={{ display: "block", color: "#888", fontSize: 14, marginBottom: 4 }}>
                                    {hasVariants ? `${activeVariant.format} Edition` : "Price"}
                                </span>
                                <span style={{ fontSize: "2.2rem", fontWeight: 700, color: "var(--primary)" }}>
                                    ${parseFloat(activeVariant.price).toFixed(2)}
                                </span>
                                {!isDigital && !isBundle && !product.isPreorder && (activeVariant.stock ?? 0) > 0 && (activeVariant.stock ?? 0) < 10 && (
                                    <p style={{ color: "#c0392b", fontSize: 12, fontWeight: 600, margin: "4px 0 0" }}>
                                        Only {activeVariant.stock} copies left!
                                    </p>
                                )}
                            </div>

                            <button
                                onClick={handleAddToCart}
                                className="admin-btn-primary"
                                style={{
                                    padding: "18px 45px", fontSize: 16,
                                    display: "flex", alignItems: "center", gap: 10,
                                    background: outOfStock ? "#bbb" : product.isPreorder ? "#e65100" : "var(--accent)"
                                }}
                                disabled={outOfStock}
                            >
                                <FiShoppingBag size={20} />
                                {outOfStock ? "Out of Stock" : product.isPreorder ? "Pre-order Now" : "Add to Cart"}
                            </button>
                        </div>

                        {/* Sharing buttons */}
                        <div style={{ marginTop: 40, paddingTop: 30, borderTop: "1px solid #eee" }}>
                            <p style={{ fontSize: 13, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Share this book</p>
                            <div style={{ display: "flex", gap: 12 }}>
                                <button
                                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank")}
                                    title="Share on Facebook"
                                    style={{ background: "#1877f2", color: "#fff", border: "none", width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                >
                                    <FiFacebook size={18} />
                                </button>
                                <button
                                    onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${product.title} by ${product.author} at LB Books!`)}&url=${encodeURIComponent(window.location.href)}`, "_blank")}
                                    title="Share on Twitter"
                                    style={{ background: "#1da1f2", color: "#fff", border: "none", width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                >
                                    <FiTwitter size={18} />
                                </button>
                                <button
                                    onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied!"); }}
                                    title="Copy Link"
                                    style={{ background: "#eee", color: "#444", border: "none", width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                >
                                    <FiLink size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reviews */}
                <ReviewSystem productId={product.id} />
            </div>
        </div>
    );
}
